import type { Custodian } from 'models/custodian';
import { connect } from 'react-redux';
import type { DealSplitList } from 'state/secondaryMarket/realtimeQuote/realtimeQuoteForm';
import {
  secondaryMarketDealSplitRowAdded,
  secondaryMarketDealSplitRowCustodianChanged,
  secondaryMarketDealSplitRowNotionalChanged,
  secondaryMarketDealSplitRowRemoved,
} from 'state/secondaryMarket/realtimeQuote/realtimeQuoteForm';
import {
  getSecondaryMarketRtFormClient,
  getSecondaryMarketRtFormDealSplitRows,
} from 'state/secondaryMarket/realtimeQuote/realtimeQuoteForm/realTimeQuoteForm.selectors';
import {
  getSecondaryMarketCustodians,
  getSecondaryMarketCustodiansError,
  hasSecondaryMarketMultipleCustodians,
} from 'state/secondaryMarket/referential/astroClient/astroClient.selectors';
import { secondaryMarketReferentialAstroClientRetryLoadingThunk } from 'state/secondaryMarket/referential/astroClient/astroClient.thunks';
import { getSecondaryMarketRealtimeAstroProductFromTab } from 'state/secondaryMarket/referential/astroProduct/astroProduct.selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { getTabIsFrozen } from 'state/workspaces/tabs/tabs.selector';

type OwnProps = Readonly<{
  tabId: string;
}>;

type StateProps = Readonly<{
  error: 'not_found' | 'error' | undefined;
  currency: string;
  custodians: readonly Custodian[];
  hasMultipleCustodians: boolean;
  dealSplitRows: DealSplitList;
  frozen: boolean;
}>;

type DispatchProps = {
  onAddNewRow: () => void;
  onRemoveRow: (rowIndex: number) => void;
  onRowCustodianChanged: (rowIndex: number, custodianId?: string) => void;
  onRowNotionalChanged: (rowIndex: number, notional: number | undefined) => void;
  onRetryLoadingCustodians: () => void;
};

export type DealSplitProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (state: AppState, { tabId }: OwnProps): StateProps => {
  const product = getSecondaryMarketRealtimeAstroProductFromTab(state, tabId);
  const client = getSecondaryMarketRtFormClient(state, tabId);
  const error = getSecondaryMarketCustodiansError(state, client?.id);
  const custodians = getSecondaryMarketCustodians(state, client?.id);
  const dealSplitRows = getSecondaryMarketRtFormDealSplitRows(state, tabId);
  const hasMultipleCustodians = hasSecondaryMarketMultipleCustodians(state, tabId);
  const frozen = getTabIsFrozen(state, tabId) ?? false;

  return {
    currency: product?.data?.currency!,
    hasMultipleCustodians,
    custodians,
    error,
    dealSplitRows,
    frozen,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId }: OwnProps): DispatchProps => {
  return {
    onRemoveRow: rowIndex => {
      dispatch(secondaryMarketDealSplitRowRemoved(tabId, rowIndex));
    },
    onAddNewRow: () => {
      dispatch(secondaryMarketDealSplitRowAdded(tabId, undefined, undefined));
    },
    onRowCustodianChanged(rowIndex, custodianId) {
      dispatch(secondaryMarketDealSplitRowCustodianChanged(tabId, rowIndex, custodianId!));
    },
    onRowNotionalChanged(rowIndex, notional) {
      dispatch(secondaryMarketDealSplitRowNotionalChanged(tabId, rowIndex, notional));
    },
    onRetryLoadingCustodians() {
      dispatch(secondaryMarketReferentialAstroClientRetryLoadingThunk(tabId));
    },
  };
};
export const connectDealSlit = connect(mapStateToProps, mapDispatchToProps);
